import { Box, Button, Menu, Stack, Typography, useTheme } from "@mui/material";
import { type MouseEvent, type ReactNode, useState } from "react";
import { DropdownMenuLinkItem } from "./DropdownMenuLinkItem";

export type DropdownMenuItem = {
  label: string;
  href?: string;
  icon?: ReactNode;
  onClick?: () => void;
};

type DropdownMenuProps = {
  buttonLabel: string;
  menuTitle?: string;
  items: DropdownMenuItem[];
  icon?: ReactNode;
};

export const DropdownMenu = ({
  buttonLabel,
  menuTitle,
  items,
  icon,
}: DropdownMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    colors,
    customFontWeight,
    customShadows,
    customSpacing,
    radius,
    width,
  } = useTheme();
  const isOpen = !!anchorEl;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const dropdownButtonStyle = {
    textDecoration: "none",
    backgroundColor: colors.background.hover,
    minWidth: 80,
    height: 40,
    borderRadius: radius.full,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundImage: `linear-gradient(to right, ${colors.text.brandPrimary}, ${colors.text.rewardGold})`,
  };

  return (
    <Box>
      <Button
        id="customized-button"
        aria-controls={isOpen ? "customized-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? "true" : undefined}
        variant="contained"
        disableElevation
        onClick={handleClick}
        sx={dropdownButtonStyle}
      >
        <Typography
          variant="body2"
          fontWeight={customFontWeight.bold}
          textTransform="none"
          color={colors.text.inverse}
        >
          {buttonLabel}
        </Typography>
        {icon && (
          <Stack
            sx={{
              alignItems: "center",
              ml: customSpacing.xs,
            }}
          >
            {icon}
          </Stack>
        )}
      </Button>

      <Menu
        id="customized-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            width: width.xs,
            borderRadius: radius.lg,
            my: customSpacing.md,
            boxShadow: customShadows.md,
          },
        }}
      >
        <Typography
          variant="overline"
          color={colors.text.secondary}
          sx={{
            pt: customSpacing.lg,
            pb: customSpacing.md,
            px: customSpacing.xl,
            height: 32,
            gap: customSpacing.md,
          }}
        >
          {menuTitle}
        </Typography>

        {items.map((item) => (
          <DropdownMenuLinkItem
            key={item.label}
            item={item}
            onClick={() => {
              if (item.onClick) {
                item.onClick();
              }
              handleClose();
            }}
          />
        ))}
      </Menu>
    </Box>
  );
};
